/**
 * toastStore.js — 전역 액션 피드백(토스트) 단일 소스
 *
 * 규칙: 사용자가 누른 버튼이 데이터를 바꾸면(저장·삭제·수정·구매·전송)
 *       성공/실패 어느 쪽이든 반드시 여기로 알린다. .claude/skills/pro-ui-hud/reference/ui_rules.md 참조.
 */
import { createStore } from '@/lib/createStore';
import { audioManager } from '@/audio/audioManager';

// `{ list }` 로 감싸 둔다 — `set({ list })` 는 목록이 그대로여도(타이머 연장·없는 id 닫기) 늘 알린다.
const store = createStore({ list: [] });   // list: [{ id, kind, msg, detail }]
const selectList = (s) => s.list;
let _seq = 0;
const _timers = new Map();

export function getToasts() { return store.get().list; }
export function subscribeToasts(fn) { return store.subscribe(fn); }
export function useToasts() { return store.useStore(selectList); }

const MAX = 4;          // 화면을 덮지 않도록 동시 표시 상한
const DEFAULT_MS = { ok: 1800, info: 2200, err: 4200 };  // 실패는 읽을 시간을 더 준다

export function dismissToast(id) {
  const t = _timers.get(id);
  if (t) { clearTimeout(t); _timers.delete(id); }
  store.set({ list: getToasts().filter((x) => x.id !== id) });
}

/**
 * @param {string} msg   한 줄 결과 ("저장했습니다")
 * @param {'ok'|'err'|'info'} kind
 * @param {{ detail?: string, ms?: number, silent?: boolean }} opt  detail = 실패 원인 등 보조 설명, silent = 거절음 생략
 */
export function pushToast(msg, kind = 'ok', opt = {}) {
  if (!msg) return null;
  // 같은 메시지가 이미 떠 있으면 쌓지 말고 타이머만 연장 (연타 방지)
  let list = getToasts();
  const dup = list.find((x) => x.msg === msg && x.kind === kind);
  const id = dup ? dup.id : ++_seq;
  if (dup) {
    const prev = _timers.get(id);
    if (prev) clearTimeout(prev);
  } else {
    list = [...list, { id, kind, msg, detail: opt.detail || null }].slice(-MAX);
    // 거절음은 「새로 뜬 실패」에만. dup 쪽(타이머 연장)에 두면 연타할 때마다 울린다.
    if (kind === 'err' && !opt.silent) audioManager.play('ui.deny');
  }
  const ms = opt.ms ?? DEFAULT_MS[kind] ?? 2200;
  _timers.set(id, setTimeout(() => dismissToast(id), ms));
  store.set({ list });
  return id;
}

export const toast = {
  ok: (msg, opt) => pushToast(msg, 'ok', opt),
  err: (msg, opt) => pushToast(msg, 'err', opt),
  info: (msg, opt) => pushToast(msg, 'info', opt),
};

// 헤드리스 검증용 훅 — capture-verify 에서 `window.__toast.ok('...')` 로 표시부만 단독 확인.
// (게임 로직은 이걸 쓰지 않는다. import 로 쓸 것.)
if (typeof window !== 'undefined') window.__toast = toast;

export default toast;
